/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * OrganizerBoardLink — the way from a board back to managing it.
 *
 * WHY IT EXISTS. An organizer looking at their own race's public board had no
 * way to reach the screen where results are imported and winners declared: they
 * had to know the /organizer/results path and find the race again by hand. The
 * attach panel is the only organizer control on this page, and it disappears
 * once every entrant has a result — exactly when there IS something to publish.
 *
 * THE SERVER DECIDES WHO SEES IT, AND IT TOOK THREE TRIES TO GET RIGHT.
 *
 * `fetchMyRaces` is NOT the test, although its name and its own comment both
 * say so: event_activity and event are PUBLIC tables, so that read answers
 * every race to anybody signed in. The first cut used it and showed
 * "Manage & publish" to an ordinary athlete. Reading the race and comparing
 * created_by_id in the browser has the same hole, and filtering a table read by
 * `id` is refused outright — the filter validator does not count the primary
 * key as a column.
 *
 * `organizer_owns_race` is scoped by {{caller_filter}} and answers one row to
 * the race's creator and nothing to anybody else.
 *
 * IT IS A LINK, NOT A PUBLISH BUTTON. Publishing announces a podium and cannot
 * be done from here, because the places have to be marked first. A button that
 * said "Publish" and then navigated somewhere else would be lying about what
 * pressing it does.
 *
 * NOT ASKED WHEN NOBODY IS SIGNED IN. The query sits behind the signed-in
 * endpoint and a 403 there raises a "Session expired" dialog over the whole
 * page — on a public board that dialog met every visitor. Same reason the
 * attach panel takes `signedIn`.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ownsRace } from './publishApi';

const INK = '#241708';
const MUTED = '#6B5844';
const HAIR = 'rgba(36, 23, 8, .12)';

export default function OrganizerBoardLink({ api, eventActivityId, signedIn }) {
  const mine = useQuery({
    queryKey: ['board', 'owns-race', eventActivityId],
    queryFn: ({ signal }) => ownsRace(api, { eventActivityId, signal }),
    enabled: !!signedIn && Number.isFinite(Number(eventActivityId)),
    // The board is read far more often than a race changes hands.
    staleTime: 60_000,
    // An athlete is not an organizer; this failing is the ordinary case here,
    // not an error worth retrying or reporting.
    retry: false,
  });

  if (!mine.data) return null;

  return (
    <section
      aria-label="Organizer actions"
      style={{
        display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
        padding: '14px 18px', marginBottom: 18,
        border: `1px solid ${HAIR}`, borderRadius: 14, background: '#FFF8F1',
      }}
    >
      <span style={{ flex: '1 1 240px', fontSize: 13, color: MUTED, lineHeight: 1.6 }}>
        This is your race. Import a results file, mark the podium and publish the
        winners from the organizer screen.
      </span>
      <Link
        to={`/organizer/results/${eventActivityId}`}
        style={{
          flex: 'none', minHeight: 42, display: 'inline-flex', alignItems: 'center',
          padding: '0 18px', borderRadius: 999, background: INK, color: '#FFF6E9',
          fontSize: 14, fontWeight: 700, textDecoration: 'none',
        }}
      >
        Manage &amp; publish results
      </Link>
    </section>
  );
}
